import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { RegistrationProgress } from '../components/RegistrationProgress';
import { AuthField, AuthShell } from '../components/auth/AuthField';
import { NotebookCover } from '../components/auth/NotebookCover';
import { notebookTitle } from '../lib/notebookTitle';

/**
 * Sign up — the web twin of mobile's register flow: the notebook cover takes
 * your name as you type it, so creating an account reads as naming a notebook.
 */
export const Register: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [name, setName] = useState('');
  const [email, setEmail] = useState(() => searchParams.get('email')?.trim() ?? '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    const trimmedName = name.trim();
    if (!trimmedName) return setError('Please enter your name.');
    if (password !== confirmPassword) return setError('Passwords do not match.');

    setLoading(true);
    try {
      await register(email.trim().toLowerCase(), password, trimmedName);
      navigate('/subscription');
    } catch (err: any) {
      setError((err?.response?.data?.detail as string) || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <RegistrationProgress step={1} />
      <div className="pb-rise mt-8 flex flex-col items-center text-center">
        <NotebookCover title={notebookTitle(name)} />
        <h1 className="pb-heading-1 m-0 mt-8 text-[2.5rem] tracking-[-0.02em] md:text-[2.75rem]">Create your notebook.</h1>
      </div>

      <form onSubmit={handleSubmit} className="mt-7 flex flex-col gap-4">
        <AuthField
          label="Your name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoComplete="name"
          placeholder="First name or full name"
          maxLength={120}
          required
        />
        <AuthField
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          placeholder="you@example.com"
          required
        />
        <AuthField
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
          placeholder="Create a password"
          required
        />
        <AuthField
          label="Confirm password"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          autoComplete="new-password"
          placeholder="Type it again"
          error={error}
          required
        />
        <button type="submit" className="pb-btn pb-btn--primary mt-2 w-full py-3.5" disabled={loading}>
          {loading ? 'Creating your notebook…' : 'Create account'}
        </button>
      </form>

      <p className="m-0 mt-8 text-center font-body text-[0.9375rem] text-on-surface-variant">
        Already have an account?{' '}
        <Link to="/login" className="font-semibold text-accent no-underline hover:underline">
          Sign in
        </Link>
      </p>
    </AuthShell>
  );
};
